import axios from 'axios';
import { handleAuthError, getAuthHeaders } from '../../../utils/authUtils';
import { API_BASE_URL } from '../../../environment';
import { cachedRequest } from '../../../utils/requestCache';

export interface TenantUser {
  tenant_user_id: number;
  tenat_name: string;
  domain_name: string;
  user_first_name: string;
  user_last_name: string;
  role_display_name: string;
  email: string;
  created_datetime: string | null;
  updated_datetime: string | null;
  is_active: boolean;
  tenant_id: number;
  user_id: number;
  role_id: number;
}

export interface PaginatedTenantUserResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: TenantUser[];
}

export const TenantUserService = {
  // Get all tenant users for a specific tenant
  getAllTenantUsers: async (): Promise<TenantUser[]> => {
    try {
      const tenantId = localStorage.getItem("selectedTenantId");
      if (!tenantId) {
        throw new Error("Tenant ID not found in localStorage.");
      }

      return await cachedRequest(`tenant-users:all:${tenantId}`, async () => {
        // Fetch all users with pagination
        let allUsers: TenantUser[] = [];
        let page = 1;
        let hasMore = true;

        while (hasMore) {
          const response = await axios.get(`${API_BASE_URL}/tenant-user/?tenant_id=${tenantId}&page=${page}&page_size=100`, {
            headers: getAuthHeaders()
          });

          const data = response.data;

          // Handle different response formats
          let users: TenantUser[] = [];
          if (Array.isArray(data)) {
            users = data;
            hasMore = false; // If it's a direct array, assume no pagination
          } else if (data.results && Array.isArray(data.results)) {
            users = data.results;
            hasMore = data.next !== null; // Check if there's a next page
          } else if (data.data && Array.isArray(data.data)) {
            users = data.data;
            hasMore = data.next !== null;
          } else {
            hasMore = false;
          }

          allUsers = [...allUsers, ...users];
          page++;

          // Safety check to prevent infinite loops
          if (page > 50) {
            hasMore = false;
          }
        }

        // Return only active users
        return allUsers.filter(user => user.is_active);
      });
    } catch (error) {
      console.error('Error fetching tenant users:', error);
      return handleAuthError(error);
    }
  },

  // Get tenant users by page (for pagination if needed)
  getTenantUsersByPage: async (page: number = 1, pageSize: number = 20): Promise<PaginatedTenantUserResponse> => {
    try {
      const tenantId = localStorage.getItem("selectedTenantId");
      if (!tenantId) {
        throw new Error("Tenant ID not found in localStorage.");
      }

      const response = await axios.get(`${API_BASE_URL}/tenant-user/?tenant_id=${tenantId}`, {
        headers: getAuthHeaders(),
        params: {
          page: page,
          page_size: pageSize
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching tenant users page:', error);
      return handleAuthError(error);
    }
  },

  // Get a specific tenant user by ID
  getTenantUserById: async (id: number): Promise<TenantUser | null> => {
    try {
      const tenantId = localStorage.getItem("selectedTenantId");
      if (!tenantId) {
        throw new Error("Tenant ID not found in localStorage.");
      }

      const response = await axios.get(`${API_BASE_URL}/tenant-user/${id}/?tenant_id=${tenantId}`, {
        headers: getAuthHeaders()
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching tenant user:', error);
      return handleAuthError(error);
    }
  }
}; 